import { HandleAnswersActions } from '@/components/decision-tree/hooks';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useSteps } from '@cmpsr/components';
import { useCallback, useEffect, useReducer, useState, type Reducer } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AnsweredQuestionsType,
  NextSurveyItemType,
  PaginationAction,
  PaginationActions,
  PaginationProps,
  PaginationResponse,
  PaginationState,
} from './types';
import { getNextSurveyItem, getPreviousSurveyItem, isFirstSurveyItem } from './usePagination.lib';

export const usePagination = ({
  steps,
  initialState,
  editQuestion = undefined,
  answersDispatch,
  submitAnswer,
  backOnFirstSurveyItem,
  isUnclearQuestionsFlow,
  unclearAnswersFlow,
  isTestResultsQuestionnaire = false,
  onItemViewed,
}: PaginationProps): PaginationResponse => {
  const firstStep = steps.findIndex(({ id }) => id === initialState.sectionId) ?? 0;
  const { activeStep, setActiveStep } = useSteps({
    index: firstStep,
    count: steps.length,
  });
  const analytics = useAnalyticsCalls();
  const [isLastSurveyItem, setIsLastSurveyItem] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingSurveyItemUpdate, setPendingSurveyItemUpdate] = useState<string | null>(null);

  // Extract survey item ID from URL
  const getSurveyItemFromUrl = useCallback((): string | null => {
    return searchParams.get('surveyItemId');
  }, [searchParams]);

  // Queue a survey item ID update instead of updating directly
  const updateUrlWithSurveyItem = useCallback(
    (surveyItemId: string) => {
      if (searchParams.get('surveyItemId') !== surveyItemId) {
        setPendingSurveyItemUpdate(surveyItemId);
      }
    },
    [searchParams]
  );

  // Apply the pending URL update in an effect
  useEffect(() => {
    if (pendingSurveyItemUpdate) {
      setSearchParams(
        (prev) => {
          prev.set('surveyItemId', pendingSurveyItemUpdate);
          return prev;
        },
        { replace: false }
      );
      setPendingSurveyItemUpdate(null);

      // Track page view when search params are updated with new surveyItemId
      analytics.callPageView({
        page_name: `${window.location.pathname}?surveyItemId=${pendingSurveyItemUpdate}`,
      });
    }
  }, [pendingSurveyItemUpdate, setSearchParams, analytics]);

  const setNextSurveyItem = (
    nextSurveyItem: NextSurveyItemType,
    answers: AnsweredQuestionsType | null,
    skipAnalytics = false
  ) => {
    const iSection = steps.findIndex((step) => step.id === nextSurveyItem.sectionId);
    if (answers) answersDispatch({ type: HandleAnswersActions.SetPreviousAnswers, payload: answers });
    answersDispatch({ type: HandleAnswersActions.ResetAnswer });
    answersDispatch({ type: HandleAnswersActions.GetPreviousAnswer, payload: nextSurveyItem.surveyItemId });
    setActiveStep(iSection);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Update URL with the survey item ID
    updateUrlWithSurveyItem(nextSurveyItem.surveyItemId);

    if (onItemViewed && !skipAnalytics) {
      onItemViewed({ sectionId: nextSurveyItem.sectionId, surveyItemId: nextSurveyItem.surveyItemId });
    } else if (!isTestResultsQuestionnaire && !skipAnalytics) {
      // TODO: extract this into a onItemViewed hook
      analytics.callOnboardingViewScreen(
        'onboarding questionnaire',
        nextSurveyItem?.sectionId ?? '',
        nextSurveyItem?.surveyItemId ?? ''
      );
    }

    return {
      currentSurveyItem: nextSurveyItem.surveyItemId,
      currentSection: nextSurveyItem.sectionId,
    };
  };

  const paginationReducer = (state: PaginationState, { type, payload }: PaginationAction): PaginationState => {
    switch (type) {
      case PaginationActions.PreviousSurveyItem: {
        const { currentSurveyItem, currentSection } = getPreviousSurveyItem(state, backOnFirstSurveyItem);

        const step = steps.findIndex((step) => step.id === currentSection);

        answersDispatch({ type: HandleAnswersActions.GetPreviousAnswer, payload: currentSurveyItem });
        setActiveStep(step);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        updateUrlWithSurveyItem(currentSurveyItem);

        return {
          ...state,
          currentSurveyItem,
          currentSection,
        };
      }
      case PaginationActions.NextSurveyItem: {
        if (!payload) return state;

        const { nextSurveyItem: nextSurveyItemRegularFlow, nextIDontKnowAnsweredQuestion, answers } = payload;
        const nextSurveyItem = isUnclearQuestionsFlow ? nextIDontKnowAnsweredQuestion : nextSurveyItemRegularFlow;

        if (!nextSurveyItem) return state;

        // TODO:What is this? Can we just use browser history now?
        const pageHistory = isUnclearQuestionsFlow
          ? unclearAnswersFlow ?? []
          : answers.length
          ? answers
          : state.pageHistory;
        const potentialNextSurveyItem = getNextSurveyItem(state);
        const isAnsweredQuestion =
          state.pageHistory.findIndex((page) => page.surveyItemId === potentialNextSurveyItem.surveyItemId) > -1 ||
          answers.some((answer) => answer.surveyItemId === nextSurveyItem.surveyItemId);

        // If the question is already answered, skip analytics
        return { ...state, ...setNextSurveyItem(nextSurveyItem, answers, isAnsweredQuestion), pageHistory };
      }
      case PaginationActions.SetSurveyItemFromUrl: {
        if (!payload || !payload.nextSurveyItem) return state;

        const { nextSurveyItem } = payload;

        return {
          ...state,
          currentSurveyItem: nextSurveyItem.surveyItemId,
          currentSection: nextSurveyItem.sectionId,
        };
      }
      default:
        return state;
    }
  };

  // Determine initial state, checking URL query param first
  const urlSurveyItemId = getSurveyItemFromUrl();
  let initialSurveyItem = editQuestion?.surveyItemId;
  let initialSectionId = editQuestion?.sectionId;

  // If URL has surveyItemId and we can find it in our steps
  if (urlSurveyItemId) {
    // Try to find the section that contains this survey item
    // Since we don't have direct access to the items, we'll use a different approach
    // We'll check if there's a section with a matching ID, which is a common pattern
    let foundSection = false;

    for (const step of steps) {
      // We're using the step ID convention to match sections with survey items
      // This is a common pattern where survey items might have an ID like "section-1-item-2"
      // or just be stored in a lookup table by their section
      if (step.id === urlSurveyItemId.split('-')[0] || step.id.includes(urlSurveyItemId.substring(0, 3))) {
        initialSurveyItem = urlSurveyItemId;
        initialSectionId = step.id;
        foundSection = true;
        break;
      }
    }

    // If we couldn't find a section match with the patterns above,
    // we'll just use the URL surveyItemId and keep the current section
    if (!foundSection && initialSectionId) {
      initialSurveyItem = urlSurveyItemId;
    }
  }

  // If no URL param or invalid URL param, fall back to initialState
  if (!initialSurveyItem) {
    initialSurveyItem = initialState.surveyItemId;
    initialSectionId = initialState.sectionId;
  }

  const firstState = {
    currentSurveyItem: initialSurveyItem,
    currentSection: initialSectionId ?? initialState.sectionId,
    pageHistory: [],
  };

  const [state, dispatch] = useReducer<Reducer<PaginationState, PaginationAction>>(paginationReducer, firstState);

  // Initialize answer for the current survey item on first load
  useEffect(() => {
    // When component first loads, make sure we retrieve the existing answer
    // This ensures answer state is populated on hard refresh
    answersDispatch({ type: HandleAnswersActions.GetPreviousAnswer, payload: initialSurveyItem });
  }, [initialSurveyItem, answersDispatch]);

  // Set URL when component first loads if not already set
  useEffect(() => {
    const currentUrlSurveyItemId = getSurveyItemFromUrl();
    if (!currentUrlSurveyItemId && state.currentSurveyItem) {
      updateUrlWithSurveyItem(state.currentSurveyItem);
    }
  }, [state.currentSurveyItem, getSurveyItemFromUrl, updateUrlWithSurveyItem]);

  // Listen for URL changes from external sources (like browser back/forward)
  useEffect(() => {
    const urlSurveyItemId = getSurveyItemFromUrl();
    if (urlSurveyItemId && urlSurveyItemId !== state.currentSurveyItem) {
      // Try to find the section for this survey item using the same pattern matching approach as above
      let sectionId = state.currentSection;
      let foundSection = false;

      for (const step of steps) {
        if (step.id === urlSurveyItemId.split('-')[0] || step.id.includes(urlSurveyItemId.substring(0, 3))) {
          sectionId = step.id;
          foundSection = true;
          break;
        }
      }

      // Only update if we found a valid section or are using the existing section
      if (sectionId) {
        // Retrieve the previous answer for this survey item before dispatching the navigation
        answersDispatch({ type: HandleAnswersActions.GetPreviousAnswer, payload: urlSurveyItemId });

        // Update the step indicator
        const step = steps.findIndex((step) => step.id === sectionId);
        setActiveStep(step);

        // Scroll to top like other navigation actions do
        window.scrollTo({ top: 0, behavior: 'smooth' });

        dispatch({
          type: PaginationActions.SetSurveyItemFromUrl,
          payload: {
            nextSurveyItem: {
              surveyItemId: urlSurveyItemId,
              sectionId: sectionId,
            },
            nextIDontKnowAnsweredQuestion: {
              surveyItemId: urlSurveyItemId,
              sectionId: sectionId,
            },
            answers: [],
          },
        });
      }
    }
  }, [
    searchParams,
    steps,
    state.currentSection,
    state.currentSurveyItem,
    getSurveyItemFromUrl,
    answersDispatch,
    setActiveStep,
  ]);

  const showBackButton = (!isFirstSurveyItem(state) || !!backOnFirstSurveyItem) && state.currentSurveyItem !== 'result';

  const goToNextSurveyItem = async () => {
    const response = await submitAnswer(state.currentSurveyItem);
    if (!response?.nextSurveyItem?.surveyItemId && !response?.nextIDontKnowAnsweredQuestion?.surveyItemId) return;
    const isTheLastSurveyItem = unclearAnswersFlow ? response.isLastIDKQuestion : response.isLastSurveyItem;
    setIsLastSurveyItem(isTheLastSurveyItem ?? false);
    dispatch({ type: PaginationActions.NextSurveyItem, payload: response });
  };

  return {
    currentPage: { currentSurveyItem: state.currentSurveyItem, currentSection: state.currentSection },
    activeStep,
    paginationDispatch: dispatch,
    goToNextSurveyItem,
    showBackButton,
    isLastSurveyItem,
  };
};
